import useVuelidate from '@vuelidate/core';
import AdvancedSelectExtended from '@/components/common/AdvancedSelectExtended/AdvancedSelectExtended.vue';
import TableviewExtended from '@/components/common/TableviewExtended/TableviewExtended.vue';
import TextEditor from '@ebg/vue-common/src/components/common/texteditor/TextEditor.vue';

const metricRow = {
  id: null,
  name: null,
  weight: null,
  valuation: null,
  comments: null,
  payout: null,
  max_payout: null
}

export default {
  name: 'PeriodMetrics',
  components: { TableviewExtended, AdvancedSelectExtended, TextEditor },
  data () {
    return {
      maxKPIRows: 5,
      isMounted: false,
      isLoading: false,
      isUpdating: false,
      paging: {
        page: 1,
        pages: 1,
        limit: 10,
        total: 0
      },
      tableSorting: { item: 'title', direction: 'asc' },
      filterValues: {
        title: null
      },
      originalTableItems: [],
      tableItems: [],
      errors: {
        texts: [],
        fields: []
      },
      metrics: null,
      periodResultData: null
    }
  },
  props: {
    userCompanyJobPositionId: {
      default: null,
      type: Number
    },
    resultData: {
      default: null,
      type: Object
    },
    mode: {
      default: null,
      type: String
    },
    canEditRow: {
      default: false,
      type: Boolean
    }
  },
  computed: {
    canSendCreationMail () {
      if (!this.periodResultData?.is_send_creation_mail_enabled) {
        return false;
      }
      if (this.allowSaving) {
        return false;
      }

      return true;
    },
    canSendValuationMail () {
      if (!this.periodResultData?.is_send_valuation_mail_enabled) {
        return false;
      }
      if (!this.allowSaving) {
        return true;
      }
      if (this.kpiResultStatuses.find(el => el.value == this.resultData?.status_id)?.code !== 'EVALUATED') {
        return false;
      }

      return true;
    },
    kpiResultStatuses () {
      return this.$store.getters.getConfigOptions('kpi_result_statuses', this.$i18n.locale);
    },
    allowSaving () {
      if (this.kpiResultStatuses.find(el => el.value == this.resultData?.status_id)?.code === 'NOT_CREATED') {
        return true;
      }

      const cleanTableItems = this.tableItems.map(({ itemIndex, ...rest }) => rest);
      if (JSON.stringify(this.originalTableItems) !== JSON.stringify(cleanTableItems)) {
        return true;
      }

      return false;
    },
    // @TODO combine with canEDit
    canEvaluate () {
      if (!this.canEditRow) {
        return false;
      }
      if (!this.resultData.is_valuation_enabled) {
        return false;
      }
      return !this.isUpdating && !this.isLoading;
    },
    canEdit () {
      if (!this.canEditRow) {
        return false;
      }
      if (this.resultData && !this.resultData.is_edit_enabled) {
        return false;
      }
      return !this.isUpdating && !this.isLoading;
    },
    requestPath () {
      const path = `kpi/results/${this.resultData.id}/?perPage=` + this.paging.limit + '&page=' + this.paging.page;
      return this.ApiRequest.addGetParamsToPath(
        path,
        (this.$refs.tableview ? this.$refs.tableview.getFilterValues() : this.filterValues),
        true
      );
    },
    tableHeaders () {
      const headers = [
        { key: 'id', label: this.$t('general.numero'), class: 'col-width-xl', type: 'slot', slotcode: 'id' },
        { key: 'salary', label: this.$t('kpi.kpi-metric-name'), class: 'col-width-xxl', type: 'slot', slotcode: 'name' },
        { key: 'weight', label: this.$t('kpi.kpi-metric-weight'), class: 'col-width-xl', type: 'slot', slotcode: 'weight' },
        { key: 'max_payout', label: this.$t('kpi.kpi-metric-max-payout'), class: 'col-width-xl' },
        { key: 'valuation', label: this.$t('kpi.kpi-metric-result'), class: 'col-width-xl', type: 'slot', slotcode: 'valuation' },
        { key: 'payout', label: this.$t('kpi.kpi-metric-payout'), class: 'col-width-xl' },
        { key: 'comments', label: this.$t('kpi.kpi-metric-comment'), class: 'col-width-xl', type: 'slot', slotcode: 'comments' }
      ];
      const actions = [];
      if (this.canEdit) {
        actions.push({ label: this.$t('general.actions.delete'), emit: 'deleteItem', bicon: 'trash', class: 'btn-danger btn-sm' });
      }
      headers.push({ key: 'actions', label: '', actions: actions, headerClass: 'col-1' });
      return headers;
    },
    tableActions () {
      return { download: false };
    },
    tableFilterConfig () {
      const filters = {};
      filters.title = { type: 'text', label: 'title', colOnlyFilter: true };
      filters.published_at = { type: 'checkbox', label: 'published_at', options: this.yesNoOptions, colOnlyFilter: true };
      return filters;
    }
  },
  methods: {
    showMetricFullText (metricId) {
      const metric = this.metrics?.filter(el => el.id == metricId);
      if (metric && metric[0]) {
        return metric[0].title;
      }
    },
    updateFilters () {
      this.BasicHelper.updateFilterValues(this.$route.name, this.filterValues);
      this.getTableData();
    },
    weightChanged (itemIndex, weight) {
      this.tableItems[itemIndex].max_payout = parseFloat((this.resultData.max_payout) * (weight / 100)).toFixed(2);
    },
    changeValuation (itemIndex, valuation) {
      const item = this.tableItems.find((el) => el.itemIndex === itemIndex);
      item.payout = parseFloat((item.weight / 100) * (valuation / 100) * this.resultData.max_payout).toFixed(2);
    },
    createNewMetric (itemIndex) {
      this.$set(
        this.tableItems.find(item => item.itemIndex === itemIndex),
        'show_metric_title',
        true
      );
    },
    changeMetric (itemIndex, metricId) {
      const selectMetricWeight = this.metrics?.find(metric => metric.id === metricId)?.weight;
      this.tableItems.find(item => item.itemIndex === itemIndex).weight = selectMetricWeight;
      this.weightChanged(itemIndex, selectMetricWeight);
    },
    loadMetrics (usedMetrics = null) {
      if (this.mode === 'mykpi' && !usedMetrics) {
        return
      }
      let path = 'kpi/metrics/';
      if (usedMetrics) {
        path += '?used_metrics=' + usedMetrics;
      }
      this.currentReq = this.ApiRequest.request(path, this.ApiRequest.REQUEST_GET, {}, (response) => {
        if (response.data) {
          this.metrics = response.data;
        }
      });
    },
    deleteItem (el) {
      if (!el.id) {
        this.tableItems.splice(parseInt(el.itemIndex), 1);
      } else {
        this.isUpdating = true;
        this.currentReq = this.ApiRequest.request(`kpi/results/${this.resultData.id}/${el.id}`, this.ApiRequest.REQUEST_DELETE, [], (response) => {
          if (!response.error) {
            this.tableItems.splice(parseInt(el.itemIndex), 1);
          }
        });
        this.isUpdating = false;
      }
    },
    validateRows () {
      this.errors = { texts: [], fields: [] };
      if (this.tableItems.length > this.maxKPIRows) {
        this.errors.texts.push(this.$t('kpi.errors.max-metrics', { maxMetricsCount: this.maxKPIRows }));
      }

      const totalWeight = Object.values(this.tableItems)
        .flat()
        .reduce((sum, item) => {
          if (item.weight) {
            sum += parseInt(item.weight);
          }
          return sum;
        }, 0);
      if (totalWeight !== 100) {
        this.errors.texts.push('<strong>' + this.$t('kpi.kpi-metric-weight') + '</strong> ' + this.$t('kpi.errors.total-must-be-100'));
      }

      this.tableItems.forEach(item => {
        if (!item.metric_id) {
          this.errors.texts.push('Norādiet KPI nosaukumu');
        }
        if (!item.weight) {
          this.errors.texts.push('Norādiet KPI svaru');
        }
      });

      return this.errors.texts.length === 0 && this.errors.fields.length === 0;
    },
    addRow () {
      if (this.tableItems.length >= this.maxKPIRows) {
        this.$store.commit('toastAdd', {
          context: 'danger',
          message: this.$t('kpi.errors.max-metrics', { maxMetricsCount: this.maxKPIRows })
        });
      } else {
        this.tableItems.push({ ...metricRow });
      }
    },
    saveRows (mode = 'update') {
      if (!this.validateRows()) {
        return;
      }
      this.isUpdating = true;
      this.currentReq = this.ApiRequest.request(`kpi/user-company-job-positions/${this.resultData.user_company_job_position}/results/${this.resultData.id}/`, this.ApiRequest.REQUEST_POST, { rows: this.tableItems, mode: mode }, (response) => {
        if (response.data) {
          this.$store.commit('toastAdd', {
            context: 'success',
            message: this.$t('general.saved')
          });
          this.tableItems = response.data.result_metrics;
          this.originalTableItems = this.originalTableItems = JSON.parse(JSON.stringify(response.data.result_metrics));
          this.$emit('updateResult', response.data.result);
          if (response.data.metrics_created) {
            this.metrics = [...this.metrics, ...response.data.metrics_created];
          }
          this.periodResultData = response.data.result;
        } else if (response.error) {
          this.ApiRequest.displayErrorDialog(response, this);
        }
        this.isUpdating = false;
      });
    },
    sendMail (mode, agreedToSendAgain = false) {
      if (!this.validateRows()) {
        return;
      }

      if (this.periodResultData[mode === 'send_created' ? 'created_mails_count' : 'valuation_mails_count'] > 0 && !agreedToSendAgain) {
        this.$refs.sendMail.show(mode, this.$t('kpi.send_again_title'), null);
        return;
      }
      this.isUpdating = true;
      const url = `kpi/user-company-job-positions/${this.resultData.user_company_job_position}/results/${this.resultData.id}/send-mails`;
      this.currentReq = this.ApiRequest.request(url, this.ApiRequest.REQUEST_POST, { mail_mode: mode }, (response) => {
        if (response.data) {
          this.$store.commit('toastAdd', {
            context: 'success',
            message: this.$t('kpi.email-sent')
          });
          this.periodResultData = {
            ...this.periodResultData,
            ...response.data
          };
        } else if (response.error) {
          this.ApiRequest.displayErrorDialog(response, this);
        }
        this.isUpdating = false;
      });
    },
    getTableData () {
      this.isLoading = false;
      if (this.currentReq) {
        this.currentReq.cancel('changed search query');
      }
      this.isLoading = true;
      this.$forceUpdate();
      this.currentReq = this.ApiRequest.request(this.requestPath, this.ApiRequest.REQUEST_GET, [], (response) => {
        if (response.data) {
          this.tableItems = response.data;
          this.originalTableItems = JSON.parse(JSON.stringify(response.data));
          if (this.mode === 'mykpi' || !this.canEditRow) {
            const usedMetrics = this.tableItems.map(item => item.metric_id).filter(id => id != null);
            this.loadMetrics(usedMetrics);
          }
          this.paging = response.paging;
          this.isLoading = false;
        }
      });
    },
    changePage (pageNr) {
      this.paging.page = pageNr;
      this.getTableData();
    }
  },
  mounted () {
    this.getTableData();
    this.loadMetrics();
    this.isMounted = true;
    this.periodResultData = this.resultData;
  },
  setup () {
    return { v$: useVuelidate() }
  }
}
